/**
 * rankingAdapter — le compte connecté (`currentUser.object`) → les classements de « Classement ».
 *
 * Port de `student/social/RankingTabScreen.js` et `RankingScreen.js` :
 *  - deux classements, « Général » (`rankings`, rang `rank`) et « Classe de {classe} »
 *    (`rankings_class`, rang `rank_class`), servis par `UserResource` (les 15 premiers) ;
 *  - une ligne par élève, rangée par sa position dans la liste (`index + 1`) : son nom affiché, son
 *    avatar, son titre, son XP (`RankingListItem`) ;
 *  - la ligne de l'élève est celle de la liste à son rang quand `rang <= longueur`
 *    (`highlightIndex`), sinon la sienne, posée en pied (`ListFooterComponent`).
 *
 * Écart voulu (maquette, `DECISIONS.md` 13/10) : sans classe renseignée, le classement général seul —
 * l'ancien écran montrait un onglet « Classe de undefined ».
 *
 * Les autres élèves sont nommés comme aujourd'hui (`display_name`) : la règle « ne jamais nommer un
 * autre élève » vaut pour le live.
 */
import { cleanMediaUrl } from "../../../../../data/constants/apiConstants"
import { type UIRankingBoard, type UIRankingEntry } from "../../../ui/types/studentGame"

export type ApiRankedStudent = {
    id?: number
    display_name?: string | null
    avatar?: string | null
    student_title?: string | null
    xp?: number | null
}

export type ApiRankingAccount = ApiRankedStudent & {
    rank?: number | null
    rank_class?: number | null
    rankings?: ApiRankedStudent[] | null
    rankings_class?: ApiRankedStudent[] | null
    profile?: { class?: string | null } | null
}

export function toUIRankingEntry(student: ApiRankedStudent | null | undefined, rank: number): UIRankingEntry | null {
    if (student?.id == null) return null
    return {
        id: student.id,
        rank,
        displayName: student.display_name ?? "",
        avatarUrl: student.avatar ? cleanMediaUrl(student.avatar) : null,
        title: student.student_title ?? null,
        xp: student.xp ?? 0,
    }
}

/**
 * Un classement — `RankingScreen.js`. L'élève est la ligne de la liste à son rang (`rang - 1`) quand
 * il y tient ; plus loin, sa propre ligne, à son rang. Sans rang servi, pas de ligne à lui : l'ancien
 * écran en posait une sans numéro.
 */
export function toUIRankingBoard(
    key: string,
    label: string,
    rows: ApiRankedStudent[] | null | undefined,
    userRank: number | null | undefined,
    account: ApiRankedStudent
): UIRankingBoard {
    const list = rows ?? []
    const entries = list
        .map((row, index) => toUIRankingEntry(row, index + 1))
        .filter((entry): entry is UIRankingEntry => entry != null)
    const rank = userRank ?? 0
    const inList = rank > 0 && rank <= list.length
    const me = inList ? toUIRankingEntry(list[rank - 1], rank) : rank > 0 ? toUIRankingEntry(account, rank) : null
    return { key, label, entries, me }
}

/** Les classements de l'élève, dans l'ordre de leurs onglets : le général, puis celui de sa classe. */
export function toUIRankingBoards(account: ApiRankingAccount | null | undefined): UIRankingBoard[] | null {
    if (account == null) return null
    const boards = [toUIRankingBoard("general", "Général", account.rankings, account.rank, account)]
    const className = account.profile?.class
    if (className != null && className !== "") {
        boards.push(
            toUIRankingBoard("class", `Classe de ${className}`, account.rankings_class, account.rank_class, account)
        )
    }
    return boards
}

/** Sans XP, le classement est fermé : « Débloque le classement ! » (`user?.xp > 0`). */
export const isRankingLocked = (account: ApiRankingAccount | null | undefined): boolean => !((account?.xp ?? 0) > 0)
